import { Badge, BlockStack, Box, Button, Divider, InlineGrid, InlineStack, Text, Tooltip } from "@shopify/polaris";
import React, { useState } from "react";
import { formatMoney } from "../../../helper_functions/utils";
import type { CostAdjustment, CostAdjustmentEditable } from "../../../api_utils/types";
import AddCostAdjustmentModal from "./AddCostAdjustmentModal";
import CostAdjustmentRow from "./CostAdjustmentRow";
import { calculateCostAdjustmentAmount } from "./calculateCostAdjustmentAmount";

function CostAdjustments({
  currency,
  lineTotal,
  purchaseOrder,
  setIsDirty,
  costAdjustments,
  setCostAdjustments,
  disabled,
  total,
}: {
  readonly currency: string;
  readonly lineTotal: number;
  readonly purchaseOrder: {
    id: number;
    supplier?: { minimum_order_value: number };
    vendorSettings?: { minimum_order_value: number };
  };
  readonly setIsDirty: (isDirty: boolean) => void;
  readonly costAdjustments: CostAdjustmentEditable[];
  readonly setCostAdjustments: (costAdjustments: CostAdjustmentEditable[]) => void;
  readonly disabled: boolean;
  readonly total: number;
}): React.JSX.Element {
  const [addAdjustmentOpen, setAddAdjustmentOpen] = useState(false);

  const updateCostAdjustment = (
    index: number,
    attributes: Partial<Pick<CostAdjustmentEditable, "amount" | "_destroy">>,
  ): void => {
    setCostAdjustments(
      costAdjustments.map((costAdjustment, costAdjustmentIndex) =>
        costAdjustmentIndex === index ? { ...costAdjustment, ...attributes } : costAdjustment,
      ),
    );
    setIsDirty(true);
  };

  const addCostAdjustment = (costAdjustment: Pick<CostAdjustment, "label" | "amount_type">): void => {
    setCostAdjustments([...costAdjustments, { ...costAdjustment, amount: "", purchase_order_id: purchaseOrder.id }]);
    setIsDirty(true);
    setAddAdjustmentOpen(false);
  };

  const taxTotal = costAdjustments
    .filter((costAdjustment) => costAdjustment.label === "Tax")
    .reduce((sum, costAdjustment) => sum + calculateCostAdjustmentAmount(costAdjustment, lineTotal), 0);
  const subtotal = lineTotal + taxTotal;
  const minimumOrderValue =
    purchaseOrder.supplier?.minimum_order_value || purchaseOrder.vendorSettings?.minimum_order_value || 0;
  const minimumOrderValueWarning = total > 0 && total < minimumOrderValue;

  const adjustmentRows = costAdjustments.map((costAdjustment, index) => ({
    costAdjustment,
    row: !costAdjustment._destroy && (
      <CostAdjustmentRow
        costAdjustment={costAdjustment}
        currency={currency}
        disabled={disabled}
        key={costAdjustment.id ?? `new-${index}`}
        lineTotal={lineTotal}
        onAmountChange={(amount) => {
          updateCostAdjustment(index, { amount });
        }}
        onRemove={() => {
          updateCostAdjustment(index, { _destroy: true });
        }}
      />
    ),
  }));

  return (
    <>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingSm">Cost Summary</Text>
          <Button
            disabled={disabled}
            onClick={() => {
              setAddAdjustmentOpen(true);
            }}
          >
            Add adjustment
          </Button>
        </InlineStack>
        <BlockStack gap="200">
          <BlockStack gap="400">
            <InlineGrid alignItems="center" columns="1fr auto 20px" gap="400">
              <Text as="span">Line total</Text>
              <Box paddingBlock="150" paddingInline="300">
                <Text alignment="end" as="p">{formatMoney(lineTotal, currency, 2)}</Text>
              </Box>
            </InlineGrid>
            {adjustmentRows.filter(({ costAdjustment }) => costAdjustment.label === "Tax").map(({ row }) => row)}
          </BlockStack>
          <Divider />
          <BlockStack gap="400">
            <InlineGrid alignItems="center" columns="1fr auto 20px" gap="400">
              <Text as="span" fontWeight="bold">Subtotal</Text>
              <Box paddingBlock="150" paddingInline="300">
                <Text alignment="end" as="p">{formatMoney(subtotal, currency, 2)}</Text>
              </Box>
            </InlineGrid>
            {adjustmentRows.filter(({ costAdjustment }) => costAdjustment.label !== "Tax").map(({ row }) => row)}
          </BlockStack>
          <Divider />
          <BlockStack gap="400">
            <InlineGrid alignItems="center" columns="1fr auto 20px" gap="400">
              <Text as="span" fontWeight="bold">Total</Text>
              <Box paddingBlock="150" paddingInline="300">
                <Text alignment="end" as="p">{formatMoney(total, currency, 2)}</Text>
              </Box>
            </InlineGrid>
            {minimumOrderValueWarning ? (
              <Tooltip content="Total is less than minimum order value">
                <Badge tone="attention">
                  {`Total bill is less than minimum order value ${formatMoney(minimumOrderValue, currency, 2)}`}
                </Badge>
              </Tooltip>
            ) : null}
          </BlockStack>
        </BlockStack>
      </BlockStack>
      {addAdjustmentOpen && !disabled ? (
        <AddCostAdjustmentModal
          onAdd={addCostAdjustment}
          onClose={() => {
            setAddAdjustmentOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

export default CostAdjustments;
